import { addDays, eachDayOfInterval, endOfMonth, startOfMonth, subDays } from 'date-fns/esm'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import type { DateCalendarPublicProps } from '@/components/Calendar'
import { selectTerritoryLicensingLimits } from '@/features/domain/territory'
import { selectUserConfiguration } from '@/features/domain/user'
import { dateUtils } from '@/utils'

import { useRecurrenceDate, useRecurrenceDispatch } from '../../../providers/StateProvider'

export function useCalendarProps() {
  const recurrenceDate = useRecurrenceDate()
  const dispatch = useRecurrenceDispatch()
  const { today } = useSelector(selectUserConfiguration)
  const { planningRange } = useSelector(selectTerritoryLicensingLimits)

  return useMemo<DateCalendarPublicProps>(() => {
    const selection = dateUtils.parseDate(recurrenceDate)
    const todayDate = dateUtils.parseDate(today)
    const minDate = startOfMonth(todayDate)
    const maxPlanningDate = addDays(todayDate, planningRange)
    const maxDate = endOfMonth(maxPlanningDate)

    // disabled days are those before today and after the planning range
    const intervalFromStartOfMonthAndToday = {
      start: minDate,
      end: subDays(todayDate, 1),
    }

    const disabledDatesBeforeToday = dateUtils.isValidInterval(intervalFromStartOfMonthAndToday)
      ? eachDayOfInterval(intervalFromStartOfMonthAndToday)
      : []

    // disabled days after the planning range to the end of the month
    const intervalFromDayAfterPlanningRangeAndMaxDate = {
      start: addDays(maxPlanningDate, 1),
      end: maxDate,
    }

    const disabledDatesAfterRange = dateUtils.isValidInterval(
      intervalFromDayAfterPlanningRangeAndMaxDate,
    )
      ? eachDayOfInterval(intervalFromDayAfterPlanningRangeAndMaxDate)
      : []

    const disabledDays = [...disabledDatesBeforeToday, ...disabledDatesAfterRange].reduce<
      Record<string, number>
    >((acc, d) => {
      acc[dateUtils.convertToYYYYMMDD(d)] = 1
      return acc
    }, {})

    return {
      layout: 'date' as const,
      months: 1,
      selection,
      today,
      showNeighboringMonth: false,
      disabledDays,
      highlightToday: true,
      minDate: dateUtils.convertToYYYYMMDD(minDate),
      maxDate: dateUtils.convertToYYYYMMDD(maxDate),

      onChange(newSelection) {
        dispatch({ type: 'recurrenceDate:change', recurrenceDate: newSelection })
      },
    }
  }, [today, dispatch, planningRange, recurrenceDate])
}
